import type React from 'react';

import { Container } from '~/components/ui/Container';
import { Reveal } from '~/components/ui/Reveal';
import { SectionHeader } from '~/components/ui/SectionHeader';
import { cn } from '~/lib/cn';
import { useContent } from '~/hooks/useContent';

/**
 * About — two-column layout (text left, values right) on desktop; stacked on mobile.
 *
 * The values column uses small non-interactive cards with a Cyan left-accent bar.
 * No stats row is rendered (content not available yet).
 */
function About(): React.ReactElement {
  const content = useContent();
  const { about } = content;

  return (
    <section
      id="about"
      className={cn('bg-[var(--color-void-navy)]', 'py-24 md:py-32', 'scroll-anchor')}
      aria-labelledby="about-heading"
    >
      <Container>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-start">
          {/* Left column — text */}
          <Reveal>
            <div className="flex flex-col gap-6">
              <SectionHeader
                eyebrow={about.eyebrow}
                heading={about.heading}
                align="left"
                id="about-heading"
              />

              <p
                className={cn(
                  'text-lg md:text-xl font-body font-light leading-[1.8]',
                  'text-[var(--color-steel-mist)]',
                  'max-w-[520px]'
                )}
              >
                {about.body}
              </p>
            </div>
          </Reveal>

          {/* Right column — values */}
          <Reveal delay={120}>
            <ul className="flex flex-col gap-4" aria-label="Company values">
              {about.values.map((value) => (
                <li
                  key={value.title}
                  className={cn(
                    'flex gap-0',
                    'bg-[var(--color-deep-panel)]',
                    'border border-[rgba(232,236,244,0.05)]',
                    'rounded-sm',
                    'overflow-hidden'
                  )}
                >
                  {/* Cyan left-accent bar */}
                  <span
                    aria-hidden="true"
                    className="flex-shrink-0 w-1 bg-[var(--color-cyan-signal)]"
                  />

                  {/* Content */}
                  <div className="flex flex-col gap-2 p-5 md:p-6">
                    <h3
                      className={cn(
                        'font-head font-semibold text-lg tracking-[0.05em]',
                        'text-[var(--color-frost-white)]'
                      )}
                    >
                      {value.title}
                    </h3>
                    <p
                      className={cn(
                        'font-body font-light text-base leading-[1.8]',
                        'text-[var(--color-steel-mist)]'
                      )}
                    >
                      {value.body}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </Container>
    </section>
  );
}

export { About };
export default About;
